import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  HStack,
  IconButton,
  Link,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FaApple, FaGooglePlay, FaLink } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';
import ScreenshotCarousel from './ScreenshotCarousel';

const SECTIONS: LearnMoreSection[] = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'features', label: 'Features' },
  { id: 'try-it-out', label: 'Try it out' },
];

const FEATURES = [
  {
    title: 'Session booking',
    description:
      'Members discover, book, and pay for training sessions in a couple of taps - availability, waitlists, and confirmations all sync with the website instantly.',
  },
  {
    title: 'Linked family accounts',
    description:
      'Parents and guardians can link junior profiles so they can manage bookings, payments, and renewals on behalf of their children without multiple logins.',
  },
  {
    title: 'Event type filtering',
    description:
      'Filter the calendar by discipline to surface only the sessions that matter to the individual member.',
  },
  {
    title: 'Trial periods for prospects',
    description:
      'Offer time-limited trial memberships so prospective members can sample club life while keeping payments and permissions under control.',
  },
  {
    title: 'Single login & automatic off-boarding',
    description:
      "Members use the same credentials for the mobile app and website. When someone doesn't renew, access is removed automatically - no admin required.",
  },
  {
    title: 'Smart refunds',
    description:
      'Members receive automatic refunds when they cancel inside the window you define. Platform fees are retained so the club never loses money.',
  },
  {
    title: 'Session cancellation',
    description:
      'Need to cancel a session? Refunds go back to every attendee at once and everyone receives a push/email notification.',
  },
  {
    title: 'Posts & polls with AI assist',
    description:
      'Publish posts, polls, and announcements, then use AI Enhance to tidy grammar, improve clarity, and keep tone consistent.',
  },
  {
    title: 'Event comments',
    description:
      'Members can drop quick questions or updates against each event to keep conversations inside the club ecosystem. Only the attendees off the event are notified of the comment. Reducing notification spam.',
  },
  {
    title: 'Club championship tracker',
    description:
      'Highlight upcoming championship races and let members log results from the app so your live leaderboard stays up to date.',
  },
  {
    title: 'Apple & Google Pay',
    description:
      'Offer Apple Pay and Google Pay so members can confirm bookings and renewals with Face ID, Touch ID, or saved cards.',
  },
  {
    title: 'Emergency contacts',
    description:
      'Key medical info and emergency contacts stay visible to coaches so they can act quickly if an incident occurs.',
  },
  {
    title: 'Partner club access',
    description:
      'Enable selected partner clubs to advertise sessions to your members with controlled, limited access.',
  },
];

const APPLE_STORE_URL = 'https://apps.apple.com/us/app/olympiq/id6755298805';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.olympiq';

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
const ClubMobileApp = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Club Mobile App' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Club mobile app
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Olympiq&apos;s mobile app gives members a single, club branded space to book sessions,
          read updates, and stay engaged with the club - without having to use multiple platforms.
        </Text>
        <Text marginTop={2} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Everything in the app syncs with the website, payments, and membership system in real
          time, so admins and prospective members see the same information everywhere.
        </Text>
      </Box>

      <Separator />

      <ScreenshotCarousel />

      <SectionBlock id="introduction" title="Introduction">
        <Text>
          The app is called Olympiq, but inside it&apos;s your club: your logo, colours, and
          content. Members use it to see the next session, book a spot, or cancel. Payments taken in
          the app show up on bank statements under your club name, not Olympiq.
        </Text>
        <Text>
          Members sign in with the same email and password they use on your club website. One login
          for the site and the app - no extra passwords to remember.
        </Text>
      </SectionBlock>

      <SectionBlock id="features" title="Features">
        <Text marginBottom={4}>
          A single app that handles everyday club admin for members, parents, admins and coaches:
        </Text>
        <VStack as="ul" gap={4} align="stretch" margin={0} padding={0} listStyleType="none">
          {FEATURES.map((feature) => (
            <HStack as="li" key={feature.title} align="start" gap={4}>
              <Box
                as="span"
                fontWeight="bold"
                fontSize="sm"
                minW={{ base: '180px', md: '220px' }}
                maxW={{ base: '180px', md: '220px' }}
                flexShrink={0}
              >
                {feature.title}
              </Box>
              <Text color="gray.700" _dark={{ color: 'gray.300' }}>
                {feature.description}
              </Text>
            </HStack>
          ))}
        </VStack>
      </SectionBlock>

      <SectionBlock id="try-it-out" title="Try it out">
        <Text>
          Download the app today and contact us for demo club login details so you can experience
          the full booking and communication flow. The demo club uses a test payment system to you
          can event test out payments without being charged!
        </Text>
        <ButtonGroup marginTop={4} flexWrap="wrap">
          <Button
            variant="outline"
            onClick={() => window.open(APPLE_STORE_URL, '_blank')}
            colorScheme="gray"
          >
            <FaApple />
            Download on the App Store
          </Button>
          <Button
            as="a"
            variant="outline"
            onClick={() => window.open(GOOGLE_PLAY_URL, '_blank')}
            colorScheme="gray"
          >
            <FaGooglePlay />
            Get it on Google Play
          </Button>
        </ButtonGroup>
        <Text marginTop={4}>
          <Link href="/contact-us" color="blue.600" fontWeight="semibold">
            Contact us
          </Link>{' '}
          for details or a guided walkthrough.
        </Text>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default ClubMobileApp;
